import {
  Users,
  Building2,
  AlertTriangle,
  TrendingUp,
  Cpu,
  Bell,
  Loader2,
  RefreshCw,
  Headphones,
  Mail,
  Phone,
  Shield,
  CheckCircle,
  MessageSquarePlus,
  Ticket,
  X,
  Send,
  Sparkles,
  Zap,
  WifiOff,
  ArrowRight,
  Plus,
  Info,
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { EarthDashboard } from '../../components/EarthDashboard';
import { NatsActivityFeed } from '../../components/NatsActivityFeed';
import { zlozKarty, zlozPasekNexus, type KluczKarty } from '../../hooks/panelGlowny';
import { useBilling } from '../../hooks/useBilling';
import { useDashboardStats } from '../../hooks/useDashboardStats';
import { ukryjSprzedazSamoobslugowa } from '../../lib/fazaSprzedazy';
import { odmien } from '../../lib/odmien';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../providers/AuthProvider';
import { useWebSocket, useWsRetryStatus } from '../../providers/WebSocketProvider';

// ─── Fireworks Canvas ────────────────────────────────────────────────────────

function FireworksCanvas({ duration = 5000 }: { duration?: number }) {
  const canvasRef = useCallback(
    (canvas: HTMLCanvasElement | null) => {
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const W = (canvas.width = canvas.offsetWidth);
      const H = (canvas.height = canvas.offsetHeight);
      const particles: Array<{
        x: number;
        y: number;
        vx: number;
        vy: number;
        alpha: number;
        color: string;
        size: number;
        decay: number;
      }> = [];

      const colors = [
        '#FFD700',
        '#FF6B6B',
        '#4ECDC4',
        '#A78BFA',
        '#F97316',
        '#22D3EE',
        '#F472B6',
        '#34D399',
        '#818CF8',
        '#FBBF24',
      ];

      function burst(x: number, y: number) {
        const count = 30 + Math.random() * 20;
        for (let i = 0; i < count; i++) {
          const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.3;
          const speed = 2 + Math.random() * 4;
          particles.push({
            x,
            y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            alpha: 1,
            color: colors[Math.floor(Math.random() * colors.length)],
            size: 2 + Math.random() * 3,
            decay: 0.015 + Math.random() * 0.02,
          });
        }
      }

      let running = true;
      const startTime = Date.now();

      // Launch bursts periodically
      const launchInterval = setInterval(() => {
        if (Date.now() - startTime > duration - 1000) {
          clearInterval(launchInterval);
          return;
        }
        burst(W * 0.2 + Math.random() * W * 0.6, H * 0.2 + Math.random() * H * 0.4);
      }, 400);

      // Initial bursts
      burst(W * 0.5, H * 0.35);
      burst(W * 0.3, H * 0.25);
      burst(W * 0.7, H * 0.3);

      let animFrameId: number;

      function animate() {
        if (!running) return;
        ctx!.clearRect(0, 0, W, H);

        for (let i = particles.length - 1; i >= 0; i--) {
          const p = particles[i];
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.04; // gravity
          p.alpha -= p.decay;

          if (p.alpha <= 0) {
            particles.splice(i, 1);
            continue;
          }

          ctx!.globalAlpha = p.alpha;
          ctx!.fillStyle = p.color;
          ctx!.beginPath();
          ctx!.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx!.fill();
        }

        ctx!.globalAlpha = 1;
        animFrameId = requestAnimationFrame(animate);
      }

      animate();

      // Cleanup after duration
      const stopTimer = setTimeout(() => {
        running = false;
        clearInterval(launchInterval);
        cancelAnimationFrame(animFrameId);
      }, duration);

      // Also stop immediately if canvas is detached (component unmount)
      // React calls ref callback with null on unmount
      (canvas as any).__fireworksCleanup = () => {
        running = false;
        clearInterval(launchInterval);
        clearTimeout(stopTimer);
        cancelAnimationFrame(animFrameId);
      };
    },
    [duration]
  );

  useEffect(() => {
    return () => {
      // On unmount, find canvas and call cleanup
      const canvasEl = document.querySelector('canvas[data-fireworks]') as HTMLCanvasElement | null;
      if (canvasEl && (canvasEl as any).__fireworksCleanup) {
        (canvasEl as any).__fireworksCleanup();
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-fireworks
      className="absolute inset-0 w-full h-full pointer-events-none z-10"
    />
  );
}

// ─── Payment Success Modal ──────────────────────────────────────────────────

function PaymentSuccessModal({ plan, onClose }: { plan: string; onClose: () => void }) {
  const planLabels: Record<string, string> = {
    starter: 'Starter',
    professional: 'Professional',
    enterprise: 'Enterprise',
  };

  return (
    <>
      {/* Wzorzec DS .scrim + .modal (jak modal ticketu niżej); klik w scrim zamyka jak dotąd */}
      <div className="scrim show" onClick={onClose} />

      {/* Fireworks — warstwa nad scrimem (ten sam poziom overlay), pod .modal */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{ zIndex: 'var(--ids-z-overlay)' }}
        aria-hidden="true"
      >
        <FireworksCanvas duration={6000} />
      </div>

      {/* Modal */}
      <div
        className="modal show"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-success-title"
      >
        <div className="modal__body text-center">
          {/* Ikona sukcesu — koło w tincie tokenowym (bez gradientu/blur) */}
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-aurora-success/15 flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-aurora-success" />
          </div>

          <h2
            id="payment-success-title"
            className="display text-3xl font-bold text-aurora-text mb-2"
          >
            Witamy w gronie klientów!
          </h2>
          <p className="text-lg text-aurora-brand font-medium mb-4">
            Plan {planLabels[plan] || plan} aktywowany
          </p>
          <p className="text-aurora-muted mb-8">
            Dziękujemy za zaufanie! Twoja subskrypcja jest już aktywna. Możesz teraz korzystać ze
            wszystkich funkcji i modułów.
          </p>

          {/* Feature highlights — jednolite tinty tokenowe (brand/success) */}
          <div className="grid grid-cols-3 gap-3 mb-8">
            <div className="p-3 bg-aurora-brand/10 rounded-ids-md border border-aurora-brand/20">
              <Sparkles className="w-5 h-5 text-aurora-brand mx-auto mb-1" />
              <p className="text-xs text-aurora-muted">Pełny dostęp</p>
            </div>
            <div className="p-3 bg-aurora-brand/10 rounded-ids-md border border-aurora-brand/20">
              <Shield className="w-5 h-5 text-aurora-brand mx-auto mb-1" />
              <p className="text-xs text-aurora-muted">Wsparcie premium</p>
            </div>
            <div className="p-3 bg-aurora-brand/10 rounded-ids-md border border-aurora-brand/20">
              <Zap className="w-5 h-5 text-aurora-success mx-auto mb-1" />
              <p className="text-xs text-aurora-muted">Marketplace modułów</p>
            </div>
          </div>

          <button onClick={onClose} className="btn btn--primary btn--lg btn--block">
            Rozpocznij pracę
          </button>
        </div>
      </div>
    </>
  );
}

interface PartnerSupport {
  id: string;
  organization_id: string | null;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string | null;
}

/** Wiersz tabeli "Lokalizacje" — podzbiór kolumn organization_sites (jak w SitesPage). */
interface DashboardSite {
  id: string;
  name: string;
  city: string;
  address: string | null;
  is_primary: boolean;
  is_active: boolean;
}

// ─── Pomocnicze (czysto prezentacyjne) ──────────────────────────────────────

/** Względny czas po polsku ("3 min temu") z realnego created_at. */
function timeAgoPl(iso: string): string {
  const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return 'przed chwilą';
  if (diffMin < 60) return `${diffMin} min temu`;
  const hours = Math.floor(diffMin / 60);
  if (hours < 24) return `${hours} godz. temu`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'wczoraj';
  return `${days} dni temu`;
}

/** Inicjały nazwy lokalizacji do avatara (avatar--sm). */
function siteInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase() || '—'
  );
}

/** Kolor kropki feedu wg severity alertu — semantyka tokenów DS bez zmian. */
const FEED_SEVERITY: Record<string, { bg: string; color: string }> = {
  critical: { bg: 'rgba(var(--ids-danger-rgb),.16)', color: 'var(--ids-danger)' },
  error: { bg: 'rgba(var(--ids-danger-rgb),.16)', color: 'var(--ids-danger)' },
  warning: { bg: 'rgba(var(--ids-warning-rgb),.16)', color: 'var(--ids-warning)' },
  success: { bg: 'rgba(var(--ids-success-rgb),.16)', color: 'var(--ids-success)' },
  info: { bg: 'rgba(var(--ids-brand-rgb),.16)', color: 'var(--ids-brand)' },
};

function feedIcon(severity: string) {
  if (severity === 'success') return CheckCircle;
  if (severity === 'info') return Info;
  return AlertTriangle;
}

export function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, currentOrganization } = useAuth();
  const { liczniki, isLoading, error, refetch } = useDashboardStats();
  const { isConnected } = useWebSocket();
  const { connectionState } = useWsRetryStatus();
  const karty = zlozKarty(liczniki);
  const pasekNexus = zlozPasekNexus(liczniki, isConnected, connectionState);
  const recentAlerts = liczniki.alerty.slice(0, 5);
  const urzadzenia = liczniki.czujniki + liczniki.licencjeUrzadzen;
  const { syncSubscription } = useBilling();
  const [partnerSupport, setPartnerSupport] = useState<PartnerSupport | null>(null);
  const [loadingPartner, setLoadingPartner] = useState(false);
  const [recentSites, setRecentSites] = useState<DashboardSite[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState('');

  // Detect payment success from URL params and trigger subscription sync
  useEffect(() => {
    if (searchParams.get('payment_success') === 'true') {
      setPaymentPlan(searchParams.get('plan') || 'professional');
      setShowPaymentSuccess(true);
      // Clean URL without triggering re-render
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('payment_success');
      newParams.delete('plan');
      newParams.delete('session_id');
      newParams.delete('module');
      setSearchParams(newParams, { replace: true });

      // Auto-sync subscription from Stripe (in case webhook didn't fire yet).
      // Give webhook 5 seconds to process before calling manual sync.
      let cancelled = false;
      const timer = setTimeout(() => {
        if (cancelled) return;
        syncSubscription()
          .then(() => {
            if (!cancelled) refetch();
          })
          .catch(() => {
            // Retry once more after 5s — webhook may still be processing
            if (cancelled) return;
            setTimeout(() => {
              if (cancelled) return;
              syncSubscription()
                .then(() => {
                  if (!cancelled) refetch();
                })
                .catch(() => {
                  /* give up silently */
                });
            }, 5000);
          });
      }, 5000);

      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, []);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketPriority, setTicketPriority] = useState<'low' | 'normal' | 'high' | 'urgent'>(
    'normal'
  );
  const [ticketCategory, setTicketCategory] = useState<
    'general' | 'technical' | 'billing' | 'feature_request' | 'bug_report' | 'other'
  >('general');
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [ticketSuccess, setTicketSuccess] = useState(false);

  // Fetch partner support info for this organization
  useEffect(() => {
    const fetchPartnerSupport = async () => {
      if (!currentOrganization?.organization_id) return;

      setLoadingPartner(true);
      try {
        // Get partner_id from organization
        const { data: orgData } = await supabase
          .from('organizations')
          .select('partner_id')
          .eq('id', currentOrganization.organization_id)
          .single();

        if (orgData?.partner_id) {
          // Fetch partner info using the partner's organization_id
          const { data: partnerData } = await supabase
            .from('partners')
            .select('id, organization_id, company_name, contact_name, email, phone')
            .eq('organization_id', orgData.partner_id)
            .eq('status', 'active')
            .single();

          if (partnerData) {
            setPartnerSupport(partnerData);
          }
        }
      } catch (err) {
        console.error('Error fetching partner support:', err);
      } finally {
        setLoadingPartner(false);
      }
    };

    fetchPartnerSupport();
  }, [currentOrganization?.organization_id]);

  // Tabela "Lokalizacje" — read-only odczyt organization_sites wzorowany na
  // SitesPage.fetchSites (te same kolumny/sortowanie), ograniczony do 5 wierszy.
  useEffect(() => {
    const fetchRecentSites = async () => {
      if (!currentOrganization?.organization_id) return;

      setLoadingSites(true);
      try {
        const { data, error: sitesError } = await supabase
          .from('organization_sites')
          .select('id, name, city, address, is_primary, is_active')
          .eq('organization_id', currentOrganization.organization_id)
          .order('is_primary', { ascending: false })
          .order('name')
          .limit(5);

        if (sitesError) throw sitesError;
        setRecentSites(data || []);
      } catch (err) {
        console.error('Error fetching sites:', err);
      } finally {
        setLoadingSites(false);
      }
    };

    fetchRecentSites();
  }, [currentOrganization?.organization_id]);

  const handleCreateTicket = async () => {
    if (!ticketSubject.trim() || !ticketDescription.trim()) return;
    if (!currentOrganization || !user) return;

    setSubmittingTicket(true);
    try {
      const { error } = await supabase.from('support_tickets').insert({
        organization_id: currentOrganization.organization_id,
        partner_id: partnerSupport?.organization_id || null,
        created_by: user.id,
        subject: ticketSubject,
        description: ticketDescription,
        priority: ticketPriority,
        category: ticketCategory,
      });

      if (error) throw error;

      setTicketSuccess(true);
      setTicketSubject('');
      setTicketDescription('');
      setTicketPriority('normal');
      setTicketCategory('general');

      setTimeout(() => {
        setShowTicketModal(false);
        setTicketSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Error creating ticket:', err);
    } finally {
      setSubmittingTicket(false);
    }
  };

  // accent — para tło+kolor chipa ikony statcardu (jak SuperAdminDashboard/BillingPage):
  // utility nadpisują bazowe rgba(brand,.15) z .statcard__ico, więc tint zgadza się z ikoną.
  // Liczby i podpisy składa `zlozKarty` (hooks/panelGlowny.ts) — tu tylko ikona,
  // etykieta i cel kliknięcia. Delty „vs ub. miesiąc” zniknęły celowo: nie ma
  // migawki, z którą dałoby się porównać (docs/02-ARCHITECTURE/PANEL_GLOWNY_PRAWDZIWE_DANE.md §2).
  const opisKart: Record<
    KluczKarty,
    { name: string; icon: typeof Cpu; accent: string; href: string }
  > = {
    urzadzenia: {
      name: t('dashboard.activeDevices'),
      icon: Cpu,
      accent: 'bg-aurora-brand/15 text-aurora-brand',
      href: '/admin/devices',
    },
    lokalizacje: {
      name: t('dashboard.sites'),
      icon: Building2,
      accent: 'bg-aurora-brand/15 text-aurora-brand',
      href: '/admin/sites',
    },
    pracownicy: {
      name: t('dashboard.workers', 'Pracownicy'),
      icon: Users,
      accent: 'bg-aurora-success/15 text-aurora-success',
      href: '/admin/workers',
    },
    alerty: {
      name: t('dashboard.activeAlerts'),
      icon: AlertTriangle,
      accent:
        liczniki.alerty.length > 0
          ? 'bg-aurora-danger/15 text-aurora-danger'
          : 'bg-aurora-success/15 text-aurora-success',
      href: '/admin/modules/neurosense',
    },
  };

  // Realne dni do końca triala (czysto prezentacyjnie, wzorzec jak w AdminLayout);
  // brak trial_ends_at ⇒ null ⇒ nagłówek CTA bez liczby (zero zmyślonych danych).
  const trialDaysLeft = currentOrganization?.organization.trial_ends_at
    ? Math.max(
        0,
        Math.ceil(
          (new Date(currentOrganization.organization.trial_ends_at).getTime() - Date.now()) /
            86_400_000
        )
      )
    : null;

  return (
    <div className="space-y-6">
      {/* PageHeader — kompozycja wg prototypu Aurora DS; tytuł spójny z nav.dashboard ("Panel główny") */}
      <div className="pagehead">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="pagehead__title">{t('dashboard.orgTitle', 'Pulpit organizacji')}</h1>
            <span className="live align-middle">
              <span className="pulse" />
              Live
            </span>
          </div>
          <p className="pagehead__sub">
            {isLoading
              ? '…'
              : `${liczniki.lokalizacjeAktywne} ${odmien(
                  liczniki.lokalizacjeAktywne,
                  'lokalizacja',
                  'lokalizacje',
                  'lokalizacji'
                )} · ${urzadzenia} ${odmien(urzadzenia, 'urządzenie', 'urządzenia', 'urządzeń')}`}
          </p>
        </div>
        <div className="pagehead__actions">
          <span
            className={`pill ${
              currentOrganization?.organization.status === 'active'
                ? 'pill--active'
                : currentOrganization?.organization.status === 'trial'
                  ? 'pill--trial'
                  : 'pill--pending'
            }`}
          >
            <span className="pin" />
            {currentOrganization?.organization.status === 'trial' && `${t('status.trial')} • `}
            {t(`plans.${currentOrganization?.organization.subscription_plan || 'free'}`)}
          </span>
          <button
            onClick={refetch}
            disabled={isLoading}
            className="btn btn--secondary"
            title={t('common.refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </button>
          <button onClick={() => navigate('/admin/sites')} className="btn btn--primary">
            <Plus className="w-4 h-4" />
            Dodaj lokalizację
          </button>
        </div>
      </div>

      {/* NEXUS — spokojna karta DS (.card z card__head/card__body), bez gradientowych warstw hero */}
      <div className="card">
        <div className="card__head">
          <span className="statcard__ico">
            <Sparkles />
          </span>
          <h2 className="card__title">NEXUS</h2>
          <span
            className={`pill ${pasekNexus.polaczony ? 'pill--active' : 'pill--pending'}`}
            title={pasekNexus.siec}
          >
            <span className="pin" />
            {pasekNexus.etykieta}
          </span>
          <button
            onClick={() => navigate('/admin/modules/nexus')}
            className="btn btn--primary ml-auto flex-shrink-0 group"
          >
            Otwórz NEXUS
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
        <div className="card__body">
          <p className="text-sm text-aurora-muted">
            Dusza Twojego systemu czuwa. Monitoruję moduły, analizuję dane i jestem gotowy do
            pomocy.
          </p>
          {/* Trzy zdania z pomiaru (hooks/panelGlowny.ts), nie literały:
              liczba modułów z module_subscriptions, stan połączenia z żywą siecią
              i otwarte alerty z RPC panel_glowny_alerty. */}
          <div
            className="flex flex-wrap items-center gap-4 mt-3 text-xs text-aurora-subtle"
            data-testid="pasek-nexus"
          >
            <div className="flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" />
              <span>{pasekNexus.moduly}</span>
            </div>
            <div className="flex items-center gap-1.5">
              {pasekNexus.polaczony ? (
                <Shield className="w-3.5 h-3.5" />
              ) : (
                <WifiOff className="w-3.5 h-3.5 text-aurora-warning" />
              )}
              <span>{pasekNexus.siec}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <AlertTriangle
                className={`w-3.5 h-3.5 ${liczniki.alerty.length > 0 ? 'text-aurora-danger' : ''}`}
              />
              <span>{pasekNexus.alerty}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-aurora-danger/10 border border-aurora-danger/30 rounded-ids-lg p-4 text-aurora-danger">
          {error}
        </div>
      )}

      {/* Stats Grid */}
      <div className="kpis" aria-busy={isLoading}>
        {isLoading && (
          <span role="status" className="sr-only">
            Ładowanie danych…
          </span>
        )}
        {karty.map((karta) => {
          const opis = opisKart[karta.klucz];
          const Ikona = opis.icon;
          return (
            <button
              type="button"
              key={karta.klucz}
              className="statcard text-left cursor-pointer hover:border-aurora-border-strong transition-colors"
              onClick={() => navigate(opis.href)}
              title={`Przejdź: ${opis.name}`}
            >
              <div className="statcard__top">
                <span className={`statcard__ico ${opis.accent}`}>
                  <Ikona />
                </span>
                <span className="statcard__lbl">{opis.name}</span>
              </div>
              {isLoading ? (
                <div className="skel h-9 w-24" aria-hidden="true" />
              ) : (
                <div className="statcard__num">{karta.wartosc}</div>
              )}
              <div className="statcard__foot">
                <span
                  className={`text-xs ${
                    karta.ton === 'uwaga'
                      ? 'text-aurora-danger'
                      : karta.ton === 'ok'
                        ? 'text-aurora-success'
                        : 'muted'
                  }`}
                >
                  {isLoading ? '…' : karta.podpis}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Mapa stanu + Aktywność — wiersz wg prototypu */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Istniejący globus 3D zamontowany w karcie (bez zmian wnętrza komponentu) */}
        <div className="card globecard lg:col-span-2">
          <div className="globecard__head">
            <h3 className="card__title">Mapa stanu — lokalizacje</h3>
          </div>
          <div className="globestage overflow-hidden" style={{ height: 440 }}>
            <div className="absolute inset-0">
              <EarthDashboard />
            </div>
          </div>
        </div>

        {/* Ostatnie alerty NeuroSense z useDashboardStats (realne created_at);
            bez znacznika Live — ten należy wyłącznie do realnego streamu NATS niżej. */}
        <div className="card">
          <div className="card__head">
            <h3 className="card__title">Ostatnie alerty</h3>
          </div>
          <div className="card__body" style={{ paddingTop: 6, paddingBottom: 6 }}>
            {recentAlerts.length === 0 ? (
              <p className="text-sm text-aurora-muted py-4">Brak ostatnich alertów.</p>
            ) : (
              <div className="feed">
                {recentAlerts.map((alert) => {
                  const dot = FEED_SEVERITY[alert.severity] ?? FEED_SEVERITY.warning;
                  const FeedIcon = feedIcon(alert.severity);
                  return (
                    <div key={alert.id} className="feed__item">
                      <span className="feed__dot" style={{ background: dot.bg, color: dot.color }}>
                        <FeedIcon />
                      </span>
                      <div>
                        <div className="feed__txt">
                          <b>{alert.device_name}</b> — {alert.message}
                        </div>
                        <div className="feed__time">{timeAgoPl(alert.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lokalizacje + szybkie akcje */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tabela lokalizacji — realne wiersze organization_sites (limit 5) */}
        <div className="card tablecard lg:col-span-2" aria-busy={loadingSites}>
          <div className="card__head">
            <h3 className="card__title">Lokalizacje</h3>
            <button
              onClick={() => navigate('/admin/sites')}
              className="btn btn--ghost btn--sm ml-auto"
            >
              {t('common.all', 'Wszystkie')}
            </button>
          </div>
          {loadingSites ? (
            <div className="p-5">
              <span role="status" className="sr-only">
                Ładowanie danych…
              </span>
              <div className="skel h-24 w-full" aria-hidden="true" />
            </div>
          ) : recentSites.length === 0 ? (
            <p className="text-sm text-aurora-muted p-5">
              Brak lokalizacji — dodaj pierwszą przyciskiem powyżej.
            </p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Lokalizacja</th>
                  <th>Miasto</th>
                  <th>Adres</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentSites.map((site) => (
                  <tr key={site.id}>
                    <td>
                      <div className="cellname min-w-0">
                        <span className="avatar avatar--sm">{siteInitials(site.name)}</span>
                        <span className="truncate">{site.name}</span>
                        {site.is_primary && <span className="badge badge--brand">Główna</span>}
                      </div>
                    </td>
                    <td>{site.city}</td>
                    <td>{site.address || '—'}</td>
                    <td>
                      <span
                        className={`pill ${site.is_active ? 'pill--active' : 'pill--suspended'}`}
                      >
                        <span className="pin" />
                        {site.is_active ? 'Aktywna' : 'Nieaktywna'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Quick Actions — lista rzędów w stylu prototypowego .navitem (hover tokenowy) */}
        <div className="card">
          <div className="card__head">
            <Bell className="w-5 h-5 text-aurora-brand" />
            <h2 className="card__title">{t('dashboard.quickActions')}</h2>
          </div>
          <div className="card__body">
            <button onClick={() => navigate('/admin/devices')} className="navitem">
              <Cpu />
              <span>{t('dashboard.addDevice')}</span>
            </button>
            <button onClick={() => navigate('/admin/users')} className="navitem">
              <Users />
              <span>{t('dashboard.inviteTeamMember')}</span>
            </button>
            <button onClick={() => navigate('/admin/sites')} className="navitem">
              <Building2 />
              <span>{t('dashboard.addSite')}</span>
            </button>
            {/* Faza 1 soft startu: bez wejścia do samoobsługowego katalogu. */}
            {!ukryjSprzedazSamoobslugowa(currentOrganization?.organization, user) && (
              <button onClick={() => navigate('/admin/modules')} className="navitem">
                <TrendingUp />
                <span>{t('dashboard.viewReports')}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* NATS Activity Feed - Live system communication */}
      <NatsActivityFeed maxHeight="380px" />

      {/* Partner Support Section — jeden akcent (success) na washu .card zamiast trzech palet */}
      {partnerSupport && (
        <div
          className="card overflow-hidden"
          style={{
            background:
              'radial-gradient(90% 120% at 0% 0%, rgba(var(--ids-success-rgb),.10), transparent 60%), var(--ids-surface)',
          }}
        >
          <div className="card__head">
            <span className="statcard__ico bg-aurora-success/15 text-aurora-success">
              <Headphones />
            </span>
            <h3 className="card__title">Dedykowany Partner Wsparcia</h3>
            <span className="pill pill--active">
              <CheckCircle className="w-3 h-3" />
              Aktywny
            </span>
          </div>
          <div className="card__body">
            <p className="text-aurora-muted text-sm mb-4">
              Masz przypisanego partnera, który zapewnia Ci stałą obsługę i wsparcie techniczne
            </p>

            {/* Partner Card */}
            <div className="bg-aurora-surface rounded-ids-lg p-4 border border-aurora-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-ids-md bg-aurora-success/15 border border-aurora-border flex items-center justify-center">
                  <Shield className="w-6 h-6 text-aurora-success" />
                </div>
                <div>
                  <p className="font-semibold text-aurora-text">{partnerSupport.company_name}</p>
                  <p className="text-sm text-aurora-muted">{partnerSupport.contact_name}</p>
                </div>
              </div>

              {/* Contact Actions — kafle info ujednolicone (subtle + pastylka success);
                      akcja "Utwórz ticket" odróżniona formą przycisku (btn--tonal), nie kolorem */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <a
                  href={`mailto:${partnerSupport.email}`}
                  className="flex items-center gap-3 px-4 py-3 bg-aurora-bg-subtle hover:bg-aurora-raised border border-aurora-border rounded-ids-md text-aurora-text transition-all"
                >
                  <div className="p-2 bg-aurora-success/15 text-aurora-success rounded-ids-md">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-aurora-subtle">Email</p>
                    <p className="text-sm font-medium truncate">{partnerSupport.email}</p>
                  </div>
                </a>
                {partnerSupport.phone && (
                  <a
                    href={`tel:${partnerSupport.phone}`}
                    className="flex items-center gap-3 px-4 py-3 bg-aurora-bg-subtle hover:bg-aurora-raised border border-aurora-border rounded-ids-md text-aurora-text transition-all"
                  >
                    <div className="p-2 bg-aurora-success/15 text-aurora-success rounded-ids-md">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-aurora-subtle">Telefon</p>
                      <p className="text-sm font-medium truncate">{partnerSupport.phone}</p>
                    </div>
                  </a>
                )}
                <button
                  onClick={() => setShowTicketModal(true)}
                  className="btn btn--tonal h-full min-h-[var(--ids-control-h)] justify-start px-4"
                >
                  <MessageSquarePlus className="w-4 h-4" />
                  Utwórz ticket
                </button>
              </div>

              {/* View Tickets Link */}
              <div className="mt-4 pt-4 border-t border-aurora-border">
                <button
                  onClick={() => navigate('/admin/support')}
                  className="btn btn--ghost btn--sm btn--block"
                >
                  <Ticket className="w-4 h-4" />
                  Zobacz wszystkie zgłoszenia
                </button>
              </div>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="px-6 py-3 bg-aurora-success/5 border-t border-aurora-border">
            <div className="flex items-center justify-center gap-6 text-xs text-aurora-muted">
              <div className="flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-aurora-success" />
                <span>Gwarantowane wsparcie</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-aurora-success" />
                <span>Certyfikowany partner</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5 text-aurora-success" />
                <span>Szybka reakcja</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading state for partner */}
      {loadingPartner && (
        <div className="card p-6">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-aurora-success animate-spin" />
            <span className="text-aurora-muted">Ładowanie informacji o wsparciu...</span>
          </div>
        </div>
      )}

      {/* Subscription CTA (for trial users) — wash wyłącznie w tokenie marki (bez surowych fioletów) */}
      {currentOrganization?.organization.status === 'trial' && (
        <div
          className="rounded-ids-xl border border-aurora-border-strong p-6"
          style={{
            background:
              'linear-gradient(90deg, rgba(var(--ids-brand-rgb),.18), rgba(var(--ids-brand-rgb),.06))',
          }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="display text-lg font-semibold text-aurora-text">
                {trialDaysLeft !== null
                  ? t('dashboard.trialEnds', { days: trialDaysLeft })
                  : t('dashboard.keepAccess')}
              </h3>
              {trialDaysLeft !== null && (
                <p className="text-aurora-muted mt-1">{t('dashboard.keepAccess')}</p>
              )}
            </div>
            <button onClick={() => navigate('/admin/billing')} className="btn btn--primary">
              {t('dashboard.upgradePlan')}
            </button>
          </div>
        </div>
      )}

      {/* Create Ticket Modal — wzorzec DS .scrim + .modal (modal__head/__body/__foot) */}
      {showTicketModal && (
        <>
          <div className="scrim show" aria-hidden="true" />
          <div
            className="modal show max-h-[90vh] overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Nowe zgłoszenie"
          >
            {ticketSuccess ? (
              <div className="modal__body text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-aurora-success/15 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-aurora-success" />
                </div>
                <h3 className="display text-xl font-semibold text-aurora-text mb-2">
                  Zgłoszenie utworzone!
                </h3>
                <p className="text-aurora-muted">Twój partner wkrótce się z Tobą skontaktuje.</p>
              </div>
            ) : (
              <>
                <div className="modal__head">
                  <MessageSquarePlus className="w-5 h-5 text-aurora-brand" />
                  <h2 className="modal__title">Nowe zgłoszenie</h2>
                  <button
                    onClick={() => setShowTicketModal(false)}
                    className="iconbtn ml-auto"
                    aria-label="Zamknij"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="modal__body space-y-4">
                  <div className="field">
                    <label className="field__label">Temat *</label>
                    <input
                      type="text"
                      value={ticketSubject}
                      onChange={(e) => setTicketSubject(e.target.value)}
                      placeholder="Krótki opis problemu..."
                      className="input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="field">
                      <label className="field__label">Kategoria</label>
                      <select
                        value={ticketCategory}
                        onChange={(e) => setTicketCategory(e.target.value as typeof ticketCategory)}
                        className="select"
                      >
                        <option value="general">Ogólne</option>
                        <option value="technical">Techniczne</option>
                        <option value="billing">Rozliczenia</option>
                        <option value="feature_request">Prośba o funkcję</option>
                        <option value="bug_report">Zgłoszenie błędu</option>
                        <option value="other">Inne</option>
                      </select>
                    </div>
                    <div className="field">
                      <label className="field__label">Priorytet</label>
                      <select
                        value={ticketPriority}
                        onChange={(e) => setTicketPriority(e.target.value as typeof ticketPriority)}
                        className="select"
                      >
                        <option value="low">Niski</option>
                        <option value="normal">Normalny</option>
                        <option value="high">Wysoki</option>
                        <option value="urgent">Pilny</option>
                      </select>
                    </div>
                  </div>

                  <div className="field">
                    <label className="field__label">Opis problemu *</label>
                    <textarea
                      value={ticketDescription}
                      onChange={(e) => setTicketDescription(e.target.value)}
                      placeholder="Opisz szczegółowo swój problem lub pytanie..."
                      rows={5}
                      className="textarea resize-none"
                    />
                  </div>

                  {partnerSupport && (
                    <div className="p-4 bg-aurora-success/10 border border-aurora-success/30 rounded-ids-md">
                      <p className="text-sm text-aurora-success">
                        Zgłoszenie zostanie wysłane do:{' '}
                        <strong>{partnerSupport.company_name}</strong>
                      </p>
                    </div>
                  )}
                </div>

                <div className="modal__foot">
                  <button onClick={() => setShowTicketModal(false)} className="btn btn--secondary">
                    Anuluj
                  </button>
                  <button
                    onClick={handleCreateTicket}
                    disabled={
                      submittingTicket || !ticketSubject.trim() || !ticketDescription.trim()
                    }
                    className="btn btn--primary"
                  >
                    {submittingTicket ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Wyślij zgłoszenie
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Payment Success Celebration Modal */}
      {showPaymentSuccess && (
        <PaymentSuccessModal plan={paymentPlan} onClose={() => setShowPaymentSuccess(false)} />
      )}
    </div>
  );
}
